import React, { useLayoutEffect, useRef, useEffect } from 'react'
import { SvgAttachIcon, SvgWarning } from '../assets/images'
import CustomButton from './CustomButton'
import { noop } from 'lodash'
import {
  ConfirmDialogWithReasonProps,
  CONFIRM_DIALOG_NAMESPACE,
  CONFIRMATION_BOX_ICON,
  DialogFormWithReason,
} from '../utils'
import CustomDialog from './CustomDialog'
import MultiLineInput from './form-input/MultiLineInput'
import { useForm } from 'react-hook-form'

const ConfirmDialogWithReason: React.FC<ConfirmDialogWithReasonProps> = ({
  open,
  showCloseButton = false,
  onCancel = noop,
  onConfirm,
  width = '',
  message,
  type,
  validationMessage,
}) => {
  const {
    register,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      reason: '',
    },
  })
  const dialogRef = useRef<HTMLDialogElement>(null)

  const onSubmit = (e: DialogFormWithReason) => {
    onConfirm(e.reason)
  }

  const showIcon = (type: string) => {
    if (type === CONFIRMATION_BOX_ICON.ATTACHMENT) {
      return <SvgAttachIcon />
    }
    return <SvgWarning />
  }

  const setBackgroundColor = (type: string) => {
    const cssClassName =
      'rounded-full h-[3.625rem] w-[3.625rem] flex items-center justify-center min-w-[3.625rem]'
    if (type === CONFIRMATION_BOX_ICON.ATTACHMENT) {
      return `bg-secondary-25 ${cssClassName}`
    }
    return `bg-red-100 ${cssClassName}`
  }

  useLayoutEffect(() => {
    dialogRef.current?.close()
  }, [open])

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.scrollTop = 0
    }
  }, [])

  return (
    <div className="z-10">
      <CustomDialog
        isOpen={open}
        onClose={onCancel}
        showCloseButton={showCloseButton}
        width={width}
      >
        <div className="w-full">
          <div>
            {/* Modal header */}
            <div className="flex items-center justify-between mb-[1.625rem] gap-3">
              <div className={setBackgroundColor(type ?? '')}>
                {showIcon(type ?? '')}
              </div>
              <div className="w-full gap-2 flex">
                <span className="text-xs/5 font-primary text-accent-800 font-light">
                  {message}
                </span>
              </div>
            </div>

            <MultiLineInput
              name="reason"
              label={CONFIRM_DIALOG_NAMESPACE.CONFIRM_REASON_LABEL}
              rows={5}
              validation={{
                required:
                  validationMessage ?? CONFIRM_DIALOG_NAMESPACE.REASON_REQUIRED,
                validate: (value) => {
                  if (!value)
                    return (
                      validationMessage ??
                      CONFIRM_DIALOG_NAMESPACE.REASON_REQUIRED
                    )
                  const trimmedValue = value.replace(/\n/g, ' ').trim()
                  return (
                    trimmedValue.length <= 50 ||
                    CONFIRM_DIALOG_NAMESPACE.REASON_LIMIT
                  )
                },
              }}
              errors={errors}
              dataQaId="confirm-dialog-with-reason-reason-input"
              register={register}
            />

            <div className="text-[14px] leading-[18px] text-right mt-1 text-accent-600">
              {CONFIRM_DIALOG_NAMESPACE.WORDS_50}
            </div>
          </div>

          <div className="border-t border-[1px] border-[#E3E3E3] mb-3 mt-3.5" />

          <div className="flex justify-end gap-3">
            <CustomButton
              type="button"
              color="others"
              className="h-[40px] gap-2 border-[1px] border-primary-500 rounded-tl-[4px] back-btn"
              isFilled={true}
              onClick={() => {
                reset()
                onCancel()
              }}
              dataQaId="confirm-dialog-with-reason-cancelbtn"
            >
              {CONFIRM_DIALOG_NAMESPACE.CANCEL}
            </CustomButton>
            <CustomButton
              type="button"
              color="others"
              width="w-fit"
              className="h-[40px] text-white rounded-tl-[4px] bg-primary-500"
              isFilled={true}
              onClick={handleSubmit(onSubmit)}
              dataQaId="confirm-dialog-with-reason-submitbtn"
            >
              {CONFIRM_DIALOG_NAMESPACE.SUBMIT}
            </CustomButton>
          </div>
        </div>
      </CustomDialog>
    </div>
  )
}

export default ConfirmDialogWithReason
